import { plural, t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import {
  BookingArrivalStatus,
  BookingCurrentEstimatedArrivalWindowRecord,
  BookingRecordDetailsCustomer,
  BookingVisitStage
} from '@navio/nsdk';
import { noddiAsync, URLKeys } from 'noddi-async';
import { cn } from 'noddi-ui-common';
import { NoddiSkeleton } from 'noddi-ui-web';
import { isToday, NoddiDateFormats, noddiFormatDate } from 'noddi-util';
import { getIsVisitLive, SERVICE_DAY_REFETCH_INTERVAL_MS } from '../../utils/bookingVisit';

export function BookingEstimatedArrivalInfo({
  booking,
  isActiveStep
}: {
  booking: BookingRecordDetailsCustomer;
  isActiveStep: boolean;
}) {
  const {
    data: currentEstimatedArrivalTime,
    isPending: isEstimatePending,
    error: currentEstimatedArrivalTimeError
  } = noddiAsync.useGet({
    type: URLKeys.getCurrentEstimatedArrivalTime,
    input: { slug: booking.slug },
    queryConfig: { refetchInterval: getIsVisitLive(booking) ? SERVICE_DAY_REFETCH_INTERVAL_MS : false }
  });

  if (isEstimatePending) {
    return <NoddiSkeleton className='h-8 w-full' />;
  }

  if (currentEstimatedArrivalTimeError) {
    return null;
  }

  return (
    <div className={cn(isActiveStep ? 'text-black' : 'text-muted-foreground', 'mb-2 flex flex-col gap-1')}>
      <RouteProgress arrival={currentEstimatedArrivalTime} />
      <ArrivalTime arrival={currentEstimatedArrivalTime} isServiceDay={isToday(booking.deliveryWindowStartsAt)} />
    </div>
  );
}

// The told window stays the promise: progress says how close we are, never a time of its own.
const RouteProgress = ({ arrival }: { arrival: BookingCurrentEstimatedArrivalWindowRecord }) => {
  const { stage, stopsBefore } = arrival;

  if (stage === BookingVisitStage.NEXT) {
    // Next from the moment the stop before starts, so not necessarily driving here yet.
    return <p className='text-sm font-semibold'>{t`You are next on today's route!`}</p>;
  }
  if (stage !== BookingVisitStage.ON_ROUTE) {
    return null;
  }
  // After a skipped stop the order is open, so the count is unknown.
  /* eslint-disable noddi/no-banned-ui-terms -- consumer copy: end customers read 'technician'; 'worker' is the backoffice term */
  const progress =
    stopsBefore == null
      ? t`Our technician is out on today's route.`
      : plural(stopsBefore, {
          one: 'Our technician has # customer left before you.',
          other: 'Our technician has # customers left before you.'
        });
  /* eslint-enable noddi/no-banned-ui-terms */
  return <p className='text-sm font-semibold'>{progress}</p>;
};

const ArrivalTime = ({
  arrival,
  isServiceDay
}: {
  arrival: BookingCurrentEstimatedArrivalWindowRecord;
  isServiceDay: boolean;
}) => {
  const { start, end, status, stage } = arrival;
  if (start && end && status) {
    const arrivalStart = noddiFormatDate(start, NoddiDateFormats.TIME);
    const arrivalEnd = noddiFormatDate(end, NoddiDateFormats.TIME);

    // An estimate, never a promise: the route moves during the day.
    if (status === BookingArrivalStatus.BEHIND) {
      return (
        <p className='text-sm'>
          <Trans>
            We are a bit behind schedule. We now think we will arrive between <strong>{arrivalStart}</strong> and{' '}
            <strong>{arrivalEnd}</strong>.
          </Trans>
        </p>
      );
    }
    if (status === BookingArrivalStatus.AHEAD) {
      return (
        <p className='text-sm'>
          <Trans>
            We are a bit ahead of schedule. We now think we will arrive between <strong>{arrivalStart}</strong> and{' '}
            <strong>{arrivalEnd}</strong>.
          </Trans>
        </p>
      );
    }
    // Once the route runs the progress line above already says we are on the way.
    if (stage === BookingVisitStage.NEXT || stage === BookingVisitStage.ON_ROUTE) {
      return (
        <p className='text-sm'>
          <Trans>
            We currently estimate that we will arrive between <strong>{arrivalStart}</strong> and{' '}
            <strong>{arrivalEnd}</strong>.
          </Trans>
        </p>
      );
    }
    return (
      <p className='text-sm'>
        <Trans>
          We currently estimate that we will arrive between <strong>{arrivalStart}</strong> and{' '}
          <strong>{arrivalEnd}</strong>. We will let you know when we are on our way.
        </Trans>
      </p>
    );
  }

  // Under way before the polled booking shows it: no window left to tell.
  if (stage === BookingVisitStage.IN_PROGRESS || stage === BookingVisitStage.DONE) {
    return null;
  }

  // On the service day "the day before" has passed: the time is still being planned.
  if (isServiceDay) {
    return (
      <p className='text-sm'>
        {t`We are still planning today's route. We'll text you your estimated arrival time as soon as it is ready.`}
      </p>
    );
  }

  return (
    <p className='text-sm'>
      {t`The day before, we will text you when we expect to arrive. We will also let you know when we are on our way.`}
    </p>
  );
};
